import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { FormError } from '@/components/ui/form-error';
import { customColorSchema, type CustomColorFormData } from '@/lib/validations';
import type { SherwinWilliamsColor } from '@/types/firestore';
import { COLOR_FAMILIES } from '@/data/sherwin-williams-colors';
import { Loader2, Save, Trash2 } from 'lucide-react';

interface EditCustomColorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  color: SherwinWilliamsColor | null;
  onSubmit: (code: string, color: CustomColorFormData) => Promise<void>;
  onDelete?: (code: string) => void;
  isSubmitting?: boolean;
  error?: string;
}

export function EditCustomColorDialog({
  open,
  onOpenChange,
  color,
  onSubmit,
  onDelete,
  isSubmitting = false,
  error,
}: EditCustomColorDialogProps) {
  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<CustomColorFormData>({
    resolver: zodResolver(customColorSchema),
    defaultValues: {
      code: '',
      name: '',
      hex: '',
      family: undefined,
      lrv: undefined,
    },
  });

  const hexValue = watch('hex');

  // Pre-populate form when color changes
  useEffect(() => {
    if (color) {
      reset({
        code: color.code,
        name: color.name,
        hex: color.hex,
        family: color.family as CustomColorFormData['family'],
        lrv: color.lrv,
      });
    }
  }, [color, reset]);

  const handleFormSubmit = async (data: CustomColorFormData) => {
    if (!color) return;
    await onSubmit(color.code, data);
  };

  const handleDelete = () => {
    if (!color || !onDelete) return;
    onDelete(color.code);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen && !isSubmitting) {
      reset();
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Custom Color</DialogTitle>
          <DialogDescription>
            Update the details of this Sherwin-Williams color.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4 mt-4">
          {/* Error message from parent */}
          {error && (
            <div className="p-3 rounded-md bg-destructive/10 border border-destructive">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Color Code */}
          <div className="space-y-2">
            <Label htmlFor="edit-code">
              Color Code <span className="text-destructive">*</span>
            </Label>
            <Input
              id="edit-code"
              placeholder="SW-7029"
              {...register('code')}
              disabled={isSubmitting}
              className={errors.code ? 'border-destructive' : ''}
            />
            <FormError message={errors.code?.message} />
            <p className="text-xs text-muted-foreground">
              Format: SW-XXXX (e.g., SW-7029)
            </p>
          </div>

          {/* Color Name */}
          <div className="space-y-2">
            <Label htmlFor="edit-name">
              Color Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="edit-name"
              placeholder="Agreeable Gray"
              {...register('name')}
              disabled={isSubmitting}
              className={errors.name ? 'border-destructive' : ''}
            />
            <FormError message={errors.name?.message} />
          </div>

          {/* Hex Color */}
          <div className="space-y-2">
            <Label htmlFor="edit-hex">
              Hex Color <span className="text-destructive">*</span>
            </Label>
            <div className="flex gap-2">
              <Input
                id="edit-hex"
                placeholder="#D1CBC0"
                {...register('hex')}
                disabled={isSubmitting}
                className={errors.hex ? 'border-destructive flex-1' : 'flex-1'}
              />
              <div
                className="w-12 h-10 rounded-md border border-input shrink-0"
                style={{
                  backgroundColor:
                    hexValue && /^#?[0-9A-Fa-f]{6}$/.test(hexValue.replace('#', ''))
                      ? hexValue.startsWith('#')
                        ? hexValue
                        : `#${hexValue}`
                      : '#e5e5e5',
                }}
                aria-label="Color preview"
              />
            </div>
            <FormError message={errors.hex?.message} />
            <p className="text-xs text-muted-foreground">
              Format: #RRGGBB (# is optional)
            </p>
          </div>

          {/* Color Family */}
          <div className="space-y-2">
            <Label htmlFor="edit-family">
              Color Family <span className="text-destructive">*</span>
            </Label>
            <Controller
              name="family"
              control={control}
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  disabled={isSubmitting}
                >
                  <SelectTrigger
                    id="edit-family"
                    className={errors.family ? 'border-destructive' : ''}
                  >
                    <SelectValue placeholder="Select family" />
                  </SelectTrigger>
                  <SelectContent>
                    {COLOR_FAMILIES.map((family) => (
                      <SelectItem key={family} value={family}>
                        {family}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            <FormError message={errors.family?.message} />
          </div>

          {/* LRV (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="edit-lrv">LRV (Light Reflectance Value)</Label>
            <Input
              id="edit-lrv"
              type="number"
              step="0.01"
              min="0"
              max="100"
              placeholder="60"
              {...register('lrv', {
                setValueAs: (v) => (v === '' ? undefined : parseFloat(v)),
              })}
              disabled={isSubmitting}
              className={errors.lrv ? 'border-destructive' : ''}
            />
            <FormError message={errors.lrv?.message} />
            <p className="text-xs text-muted-foreground">Optional. Range: 0-100</p>
          </div>

          {/* Actions */}
          <DialogFooter className="flex-col sm:flex-row gap-2 pt-4">
            {onDelete && (
              <Button
                type="button"
                variant="destructive"
                onClick={handleDelete}
                disabled={isSubmitting}
                className="sm:mr-auto"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete Color
              </Button>
            )}
            <div className="flex gap-2 w-full sm:w-auto">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={isSubmitting}
                className="flex-1 sm:flex-none"
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting} className="flex-1 sm:flex-none">
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
